import { Auth, onAuthStateChanged, User } from 'firebase/auth';
import {
	DocumentData,
	DocumentReference,
	getCountFromServer,
	onSnapshot,
	Query,
} from 'firebase/firestore';
import { from, Observable } from 'rxjs';

export interface RxFirestoreOptions {
	/** When provided, the document id is written onto the emitted data under this key. */
	idField?: string;
}

/**
 * Streams the documents of a query as a realtime array. Replaces
 * `@angular/fire`'s `collectionData` using the modular SDK's `onSnapshot`.
 */
export function collectionData<T = DocumentData>(
	query: Query<T>,
	options?: RxFirestoreOptions,
): Observable<T[]> {
	return new Observable<T[]>((subscriber) => {
		const unsubscribe = onSnapshot(
			query,
			(snapshot) => {
				const data = snapshot.docs.map((doc) => {
					const value = doc.data();
					if (options?.idField) {
						(value as Record<string, unknown>)[options.idField] =
							doc.id;
					}
					return value;
				});
				subscriber.next(data);
			},
			(error) => subscriber.error(error),
		);
		return { unsubscribe };
	});
}

/**
 * Streams a single document as a realtime value. Replaces `@angular/fire`'s
 * `docData` using the modular SDK's `onSnapshot`. Emits `undefined` when the
 * document does not exist (matching the previous behavior).
 */
export function docData<T = DocumentData>(
	ref: DocumentReference<T>,
	options?: RxFirestoreOptions,
): Observable<T> {
	return new Observable<T>((subscriber) => {
		const unsubscribe = onSnapshot(
			ref,
			(snapshot) => {
				const value = snapshot.data();
				if (value && options?.idField) {
					(value as Record<string, unknown>)[options.idField] =
						snapshot.id;
				}
				subscriber.next(value as T);
			},
			(error) => subscriber.error(error),
		);
		return { unsubscribe };
	});
}

/**
 * Emits the server-side count of a query. Replaces `@angular/fire`'s
 * `collectionCount`, which resolved once from `getCountFromServer`.
 */
export function collectionCount<T = DocumentData>(
	query: Query<T>,
): Observable<number> {
	return from(getCountFromServer(query).then((snap) => snap.data().count));
}

/**
 * Streams the current Firebase auth user. Replaces `@angular/fire`'s `authState`.
 */
export function authState(auth: Auth): Observable<User | null> {
	return new Observable<User | null>((subscriber) => {
		const unsubscribe = onAuthStateChanged(
			auth,
			(user) => subscriber.next(user),
			(error) => subscriber.error(error),
		);
		return { unsubscribe };
	});
}
